import { CITY_GUIDES } from "@/content/geo/cities";
import { MODEL_GUIDES } from "@/content/geo/models";
import { USE_CASE_GUIDES } from "@/content/geo/useCases";
import { COMPARISON_GUIDES } from "@/content/geo/comparisons";
import { HIRE_LANDINGS, hireLandingPath } from "@/content/geo/hireLandings";
import { canonicalCityGuidePath } from "../../../../lib/copy/src/city-guide-policy.mjs";
import { publishedLandingSourcePaths, compiledPublishedLandingSource } from
  "../../../../lib/copy/src/published-landing-bindings.mjs";

/**
 * Publication records from the browser's authored content, not build snapshots.
 * CMS-owned reviewed replacements retain their existing source precedence.
 */
export function compiledPublicSourcePaths(context?: { origin?: string }) {
  return [
    ...CITY_GUIDES.map(guide => canonicalCityGuidePath(guide.slug)).filter((value): value is string => !!value),
    ...MODEL_GUIDES.map(guide => `/models/${guide.slug}`),
    ...USE_CASE_GUIDES.map(guide => `/hire/${guide.slug}`),
    ...COMPARISON_GUIDES.map(guide => `/compare/${guide.slug}`),
    ...HIRE_LANDINGS.map(entry => `/${entry.market}${hireLandingPath(entry)}`),
    "/models", "/cities", "/hire", "/compare", "/apex-score",
    ...publishedLandingSourcePaths(context),
  ];
}
export function readCompiledPublicSource(pathname: string, context?: { origin?: string },
  loadViewModel?: (path: string) => unknown) {
  const published = compiledPublishedLandingSource(pathname, context, loadViewModel);
  if (published) return published;
  const city = CITY_GUIDES.find(guide => canonicalCityGuidePath(guide.slug) === pathname);
  const model = MODEL_GUIDES.find(guide => `/models/${guide.slug}` === pathname);
  const useCase = USE_CASE_GUIDES.find(guide => `/hire/${guide.slug}` === pathname);
  const comparison = COMPARISON_GUIDES.find(guide => `/compare/${guide.slug}` === pathname);
  const editorial = city || model || useCase || comparison;
  const index = ["/models", "/cities", "/hire", "/compare"].includes(pathname);
  const hire = HIRE_LANDINGS.find(entry =>
    `/${entry.market}${hireLandingPath(entry)}` === pathname);
  if (!editorial && !hire && !index) return null;
  if (pathname === "/de/hochzeitsauto-mieten") return null;
  const sourceReference = city ? `authored-city:${city.slug}` :
    editorial ? `authored-geo:${pathname}` : index ? `authored-geo-index:${pathname}` : `authored-hire:${pathname}`;
  return {
    sourceReference,
    preferCms: pathname === "/dk/bryllupsbil",
    row: {
      kind: "editorial",
      contentIdentity: sourceReference,
      canonicalPath: hire?.canonicalPath || pathname,
      publication: "published",
      indexable: true,
      language: hire?.locale || "en",
      languageVerified: true,
    },
    ...((editorial || hire) ? { meta: {
      title: (editorial || hire)!.metaTitle,
      description: (editorial || hire)!.metaDescription,
      ogImage: editorial?.hero.image || hire?.heroImage,
    } } : {}),
  };
}

/** The same authoring record selects the body on SSR and client navigation.
 * An unrelated legacy CMS projection cannot replace a registered template. */
export function readPublicTemplateDecision(): unknown {
  if (typeof document === "undefined") return null;
  const node = document.getElementById("public-route-decision");
  if (!node?.textContent) return null;
  try { return JSON.parse(node.textContent); }
  catch { return null; }
}

export function prefersCompiledPublicTemplate(pathname: string, {
  seed, decision,
}: { seed?: unknown; decision?: unknown } = {}): boolean {
  const source = readCompiledPublicSource(pathname);
  if (!source) return false;
  // SSR receives the actual resolved view model, including a registered
  // fallback when the preferred CMS record was successfully read as absent.
  if (seed && typeof seed === "object" && "kind" in seed) {
    if (seed.kind === "compiled") return true;
    if (seed.kind === "landing") return false;
  }
  // The initial browser uses that same resolver's path-bound projection, not
  // a previous page's DOM record after client navigation.
  if (decision && typeof decision === "object" &&
      "pathname" in decision && decision.pathname === pathname &&
      "kind" in decision && decision.kind === "page" &&
      "status" in decision && decision.status === 200 &&
      "contentIdentity" in decision && typeof decision.contentIdentity === "string")
    return decision.contentIdentity === source.sourceReference;
  return !source.preferCms;
}
